// Drain-beacon telemetry: summarise a held-work batch a rescue just shipped
// off-device (main #829). Extracted from rescue-held-captures.ts so that file stays
// within the line cap. Pure + reusable; carries NO PII (counts + a building COUNT +
// internal reason codes only), so a caller can put the result straight into a Sentry
// `extra`.

import type { RecoveryDumpItem } from './rescue-held-captures'

/** Aggregate magnitude + context of a drained held-work batch, for telemetry. */
export interface DrainSummary {
  /** Σ quantity that moved off-device (a null quantity counts as 0). */
  qty: number
  /** Number of DISTINCT buildings the counts belong to. */
  buildings: number
  /** DISTINCT internal reason codes, sorted for a stable telemetry fingerprint. */
  reasons: string[]
}

/**
 * Summarise a held-work batch (the items a rescue just shipped) for the drain beacon:
 * Σquantity (the contractual count that drained off-device), the count of DISTINCT
 * buildings (each item's own `buildingId`, falling back to its session's building when
 * the count's own FK context was already gone), and the DISTINCT reason codes (sorted,
 * so the telemetry fingerprint is stable run to run).
 */
export function summarizeDrain(items: readonly RecoveryDumpItem[]): DrainSummary {
  const buildings = new Set<string>()
  const reasons = new Set<string>()
  let qty = 0
  for (const item of items) {
    qty += Number(item.quantity) || 0
    const building = item.buildingId ?? item.sessionBuildingId
    if (building != null) buildings.add(building)
    if (item.reason != null) reasons.add(item.reason)
  }
  return { qty, buildings: buildings.size, reasons: [...reasons].sort() }
}
